import { useEffect, useState } from "react";
import { Form, message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  fetchEmployeeNumbers,
  fetchJobPostings,
  fetchEmployeeData,
  fetchFeedbackEmployees,
  submitEmployeeDocuments,
} from "Services/AdminServices/DocumentsStoreService";
import { toast } from "react-toastify";

export default function useDocumentsStore() {
  const [feedbackEmployees, setFeedbackEmployees] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [selectedEmployeeNumber, setSelectedEmployeeNumber] = useState(null);
  const [selectedJobPostingId, setSelectedJobPodtingId] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [files, setFiles] = useState({
    ndafile: null,
    ncafile: null,
    employeeAgreement: null,
    i_nine: null,
    w_four: null,
    offerLetter: null,
    hrpolicy: null,
  });

  useEffect(() => {
    const getJobPostings = async () => {
      const jobPostings = await fetchJobPostings();
      setJobPostings(jobPostings);
    };
    getJobPostings();
  }, []);

  useEffect(() => {
    const getEmployeeNumbers = async () => {
      const applicants = await fetchEmployeeNumbers();
      setFeedbackEmployees(applicants);
    };
    getEmployeeNumbers();
  }, []);

  const handleJobPostingChange = async (value) => {
    const jobPostingId = value != null ? Number(value) : null;
    setSelectedJobPodtingId(jobPostingId);
    const feedbackEmployees = await fetchFeedbackEmployees(jobPostingId);
    setFeedbackEmployees(feedbackEmployees);
    return;
  };

  const handleFeedbackEmployeeChange = async (jobApplicantId) => {
    const selectedId = jobApplicantId != null ? Number(jobApplicantId) : null;
    setSelectedEmployeeNumber(selectedId);
    try {
      const employee = await fetchEmployeeData(selectedId);
      setEmployeeDetails(employee);
    } catch (error) {
      console.error("Error fetching employee details:", error);
    }
  };

  const handleFileChange = (fileType) => (info) => {
    const file = info.fileList[0]?.originFileObj;
    if (file) {
      // size validation
      const maxSize = fileType === "hrpolicy" ? 40 * 1024 * 1024 : 2 * 1024 * 1024;
      if (file.size > maxSize) {
        message.error(`File size exceeds the limit of ${maxSize / (1024 * 1024)} MB.`);
        return;
      }
      setFiles((prev) => ({
        ...prev,
        [fileType]: file,
      }));
    } else {
      setFiles((prev) => ({
        ...prev,
        [fileType]: null,
      }));
    }
  };

  const handleFormSubmit = async () => {
    if (!selectedEmployeeNumber) {
      message.error("Please select an employee before submitting!");
      return;
    }

    const requiredFiles = [
      "ndafile",
      "ncafile",
      "employeeAgreement",
      "i_nine",
      "w_four",
      "offerLetter",
      "hrpolicy",
    ];
    const missingFiles = requiredFiles.filter((file) => !files[file]);
    if (missingFiles.length > 0) {
      message.error(`Missing required files: ${missingFiles.join(", ")}`);
      return;
    }

    const formData = new FormData();
    formData.append("JobApplicantId", Number(selectedEmployeeNumber)); // API requires int

    formData.append("Ndafile", files.ndafile);
    formData.append("Ncafile", files.ncafile);
    formData.append("EmployeeAgreement", files.employeeAgreement);
    formData.append("I_nine", files.i_nine);
    formData.append("W_four", files.w_four);
    formData.append("OfferLetter", files.offerLetter);
    formData.append("HRPolicy", files.hrpolicy);

    try {
      setLoading(true);
      await submitEmployeeDocuments(formData);
      message.success("Documents submitted successfully!");
      toast.success("Documents submitted successfully!")
      navigate("/dashboard");
    } catch (err) {
      message.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled = !selectedEmployeeNumber;

  const handleDocumentClick = (item) => {
    const fileExtension = item.pdfUrl.split(".").pop();

    if (fileExtension === "docx") {
      const link = document.createElement("a");
      link.href = item.pdfUrl;
      link.download = item.title;
      link.click();
    } else if (fileExtension === "pdf") {
      navigate("/document-preview", { state: { pdfUrl: item.pdfUrl, title: item.title } });
    }
  };

  return {
    selectedEmployeeNumber,
    form,
    jobPostings,
    feedbackEmployees,
    loading,
    handleDocumentClick,
    isSubmitDisabled,
    handleFormSubmit,
    handleFileChange,
    handleJobPostingChange,
    handleFeedbackEmployeeChange,
    employeeDetails,
  };
}
